import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, CircularProgress, List, ListItemButton,
    ListItemIcon, ListItemText, Collapse, Paper, Typography,
    TextField, Divider, IconButton
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { fetchClientDriveFolders, createFolder } from "../../services/drive.service";

interface MoveItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMove: (targetFolderId: string) => void;
    clientId: string;
    selectedItem: any;
}

interface InlineCreateState {
    isCreating: boolean;
    parentId: string;
}

// --- RECURSIVE TREE NODE COMPONENT ---
const FolderTreeNode = ({ folder, selectedId, onSelect, level = 0, onInlineSubmit, onInlineCancel }: any) => {
    const [isOpen, setIsOpen] = useState(true);
    const [tempName, setTempName] = useState(folder.name);
    const hasChildren = folder.children && folder.children.length > 0;

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    // INLINE INPUT RENDERING FOR NEW FOLDER (UPDATED WITH BUTTONS)
    if (folder.isTemp) {
        return (
            <ListItemButton sx={{
                pl: 1 + level * 3,
                py: 0.5,
                borderRadius: 1,
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5 // Adds slight spacing between the input and buttons
            }}
                disableRipple // Prevent ripple effect when typing/clicking inside
            >
                <Box sx={{ width: 24 }} /> {/* Spacer to align with other folders */}
                <ListItemIcon sx={{ minWidth: 32 }}>
                    <FolderIcon fontSize="small" sx={{ color: "#fbc02d" }} />
                </ListItemIcon>
                <TextField
                    size="small"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    autoFocus
                    onFocus={(e) => e.target.select()} // Auto-selects "New folder" text
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            onInlineSubmit(tempName);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            onInlineCancel();
                        }
                    }}
                    // Removed onBlur so it doesn't conflict with button clicks
                    sx={{
                        '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.875rem' },
                        backgroundColor: 'background.paper',
                        flexGrow: 1
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
                {/* Save Button */}
                <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onInlineSubmit(tempName);
                    }}
                    sx={{ p: 0.5 }}
                >
                    <CheckIcon fontSize="small" />
                </IconButton>
                {/* Cancel Button */}
                <IconButton
                    size="small"
                    color="default"
                    onClick={(e) => {
                        e.stopPropagation();
                        onInlineCancel();
                    }}
                    sx={{ p: 0.5 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </ListItemButton>
        );
    }

    // NORMAL FOLDER RENDERING
    return (
        <>
            <ListItemButton
                selected={selectedId === folder._id}
                onClick={() => onSelect(folder._id)}
                sx={{
                    pl: 1 + level * 3,
                    py: 0.5,
                    borderRadius: 1,
                    mb: 0.5,
                    "&.Mui-selected": { backgroundColor: "rgba(25, 118, 210, 0.12)" }
                }}
            >
                {/* Expand/Collapse Arrow */}
                <Box
                    onClick={hasChildren ? handleToggle : undefined}
                    sx={{ width: 24, display: 'flex', alignItems: 'center', cursor: hasChildren ? 'pointer' : 'default' }}
                >
                    {hasChildren ? (
                        isOpen ? <KeyboardArrowDownIcon fontSize="small" color="action" /> : <KeyboardArrowRightIcon fontSize="small" color="action" />
                    ) : null}
                </Box>

                <ListItemIcon sx={{ minWidth: 32 }}>
                    <FolderIcon fontSize="small" sx={{ color: "#fbc02d" }} />
                </ListItemIcon>

                <ListItemText
                    primary={folder.name}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: selectedId === folder._id ? 600 : 400 }}
                />
            </ListItemButton>

            {/* Render Children Recursively */}
            {hasChildren && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {folder.children.map((child: any) => (
                            <FolderTreeNode
                                key={child._id}
                                folder={child}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                level={level + 1}
                                onInlineSubmit={onInlineSubmit}
                                onInlineCancel={onInlineCancel}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

// --- MAIN MODAL COMPONENT ---
export default function MoveItemModal({ isOpen, onClose, onMove, clientId, selectedItem }: MoveItemModalProps) {
    const [folders, setFolders] = useState<any[]>([]);
    // The client's own root folder always has a real parentId in this client-scoped list
    // (it's nested under "Clients"), so it can't be spotted by looking for a falsy parentId —
    // the backend hands the id back explicitly instead of leaving callers to guess it.
    const [rootFolderId, setRootFolderId] = useState<string | null>(null);
    const [targetId, setTargetId] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingAPI, setIsCreatingAPI] = useState(false);

    // Simplified inline state
    const [inlineCreateState, setInlineCreateState] = useState<InlineCreateState>({
        isCreating: false,
        parentId: ""
    });

    useEffect(() => {
        if (isOpen && clientId) {
            fetchClientFolders();
            setTargetId("");
            setInlineCreateState({ isCreating: false, parentId: "" });
        }
    }, [isOpen, clientId]);

    const fetchClientFolders = async () => {
        setIsLoading(true);
        try {
            const res: any = await fetchClientDriveFolders(clientId);
            if (res && !res.hasError) {
                setFolders(res.folders || []);
                setRootFolderId(res.rootId || null);
            }
        } catch (error) {
            console.error("Failed to fetch folders", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNewFolder = () => {
        const parentId = targetId || rootFolderId;
        if (!parentId) {
            alert('Still loading this client\'s folders — please wait a moment and try again.');
            return;
        }
        setInlineCreateState({
            isCreating: true,
            parentId
        });
    };

    const handleInlineSubmit = async (name: string) => {
        if (!inlineCreateState.isCreating) return; // Prevent double-fire from onBlur and onKeyDown

        const finalName = name.trim() || "New folder";
        const parentToUse = inlineCreateState.parentId;

        // Immediately hide input, show loading state
        setInlineCreateState({ isCreating: false, parentId: "" });
        setIsCreatingAPI(true);

        try {
            const response: any = await createFolder(parentToUse, finalName, clientId);

            if (response && !response.hasError) {
                await fetchClientFolders();
                if (response.folder?._id) {
                    setTargetId(response.folder._id);
                }
            }
        } catch (error) {
            console.error("Failed to create folder:", error);
            alert("Failed to create folder. It might already exist.");
        } finally {
            setIsCreatingAPI(false);
        }
    };

    const handleInlineCancel = () => {
        setInlineCreateState({ isCreating: false, parentId: "" });
    };

    // --- BUILD THE HIERARCHICAL TREE ---
    const folderTree = useMemo(() => {
        if (!folders.length && !inlineCreateState.isCreating) return [];

        // 1. Filter out the item being moved
        const safeFolders = folders.filter((f) => {
            if (f._id === selectedItem?._id) return false;
            if (selectedItem?.isFolder && f.path && selectedItem.path && f.path.startsWith(selectedItem.path + '/')) {
                return false;
            }
            return true;
        });

        // 2. INJECT TEMPORARY NEW FOLDER NODE
        if (inlineCreateState.isCreating) {
            safeFolders.push({
                _id: "temp-new-folder-id",
                name: "New folder",
                parentId: inlineCreateState.parentId,
                isTemp: true // Flag to tell FolderTreeNode to render an input
            });
        }

        // 3. Map and connect children
        const folderMap: Record<string, any> = {};
        safeFolders.forEach((f) => {
            folderMap[f._id] = { ...f, children: [] };
        });

        const roots: any[] = [];
        safeFolders.forEach((f) => {
            if (f.parentId && folderMap[f.parentId]) {
                folderMap[f.parentId].children.push(folderMap[f._id]);
            } else {
                roots.push(folderMap[f._id]);
            }
        });

        return roots;
    }, [folders, selectedItem, inlineCreateState]);

    const handleMove = () => {
        if (targetId) onMove(targetId);
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Move "{selectedItem?.name}"</DialogTitle>

            <DialogContent sx={{ pt: 1, pb: 2, minHeight: "300px", display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select the destination folder:
                </Typography>

                {/* Cleaned up Create New Folder Button */}
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={isCreatingAPI ? <CircularProgress size={16} /> : <CreateNewFolderIcon />}
                        onClick={handleCreateNewFolder}
                        size="small"
                        disabled={inlineCreateState.isCreating || isCreatingAPI}
                        sx={{ mb: 1 }}
                    >
                        {isCreatingAPI ? "Creating..." : "Create New Folder"}
                    </Button>
                    <Divider />
                </Box>

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <CircularProgress size={30} />
                    </Box>
                ) : (
                    <Paper variant="outlined" sx={{ flexGrow: 1, maxHeight: "400px", overflowY: "auto", p: 1, bgcolor: "#fafafa" }}>
                        <List component="nav" disablePadding>
                            {folderTree.map((rootNode) => (
                                <FolderTreeNode
                                    key={rootNode._id}
                                    folder={rootNode}
                                    selectedId={targetId}
                                    onSelect={setTargetId}
                                    level={0}
                                    onInlineSubmit={handleInlineSubmit}
                                    onInlineCancel={handleInlineCancel}
                                />
                            ))}

                            {folderTree.length === 0 && (
                                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                                    No eligible folders found.
                                </Box>
                            )}
                        </List>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions sx={{ pb: 2, pr: 3 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleMove}
                    variant="contained"
                    disabled={!targetId || isLoading || isCreatingAPI || inlineCreateState.isCreating}
                >
                    Move Here
                </Button>
            </DialogActions>
        </Dialog>
    );
}